import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Button,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Alert,
  CircularProgress,
  Chip,
  TextField,
} from '@mui/material';
import {
  AccessTime as ClockIcon,
  Login as ClockInIcon,
  Logout as ClockOutIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../services/api';
import TimeclockAdmin from './TimeclockAdmin';
import { formatDateInZone, formatTimeInZone } from '../utils/timezone';

interface TimeEntry {
  id: number;
  clock_in: string;
  clock_out: string | null;
  clock_in_timezone?: string | null;
  clock_out_timezone?: string | null;
  hours_worked: number | string | null;
  notes: string | null;
}

const Timeclock: React.FC = () => {
  const queryClient = useQueryClient();
  const [user, setUser] = useState<any>(null);
  const [notes, setNotes] = useState('');
  const [currentTime, setCurrentTime] = useState(new Date());

  useEffect(() => {
    const userStr = localStorage.getItem('user');
    
    if (userStr) {
      try {
        const parsedUser = JSON.parse(userStr);
        setUser(parsedUser);
      } catch (e) {
        console.error('Error parsing user:', e);
      }
    }
  }, []);

  // Update current time every second
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  // Fetch active entry
  const { data: activeData, isLoading: activeLoading } = useQuery({
    queryKey: ['activeTimeEntry'],
    queryFn: () => api.getActiveTimeEntry(),
    refetchInterval: 10000, // Refresh every 10 seconds
    enabled: !!user && user.role !== 'manager' && user.role !== 'superuser',
  });

  // Fetch current week entries
  const { data: weekData, isLoading: weekLoading } = useQuery({
    queryKey: ['currentWeekEntries'],
    queryFn: () => api.getCurrentWeekEntries(),
    refetchInterval: 30000, // Refresh every 30 seconds
    enabled: !!user && user.role !== 'manager' && user.role !== 'superuser',
  });

  // Clock in mutation (organization is handled by backend from authenticated user)
  const clockInMutation = useMutation({
    mutationFn: () => api.clockIn(undefined, notes),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['activeTimeEntry'] });
      queryClient.invalidateQueries({ queryKey: ['currentWeekEntries'] });
      setNotes('');
    },
  });

  // Clock out mutation
  const clockOutMutation = useMutation({
    mutationFn: () => api.clockOut(),
    onSuccess: async () => {
      // Directly update cache to show no active entry
      queryClient.setQueryData(['activeTimeEntry'], { entry: null });
      // Force refetch of week entries
      await queryClient.invalidateQueries({ queryKey: ['currentWeekEntries'] });
      await queryClient.refetchQueries({ queryKey: ['currentWeekEntries'] });
    },
  });

  const handleClockIn = () => {
    clockInMutation.mutate();
  };

  const handleClockOut = () => {
    clockOutMutation.mutate();
  };

  const calculateElapsedTime = (clockInTime: string) => {
    const start = new Date(clockInTime);
    const diff = currentTime.getTime() - start.getTime();
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((diff % (1000 * 60)) / 1000);
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  };

  // If manager or superuser, show them the admin view directly
  if (user && (user.role === 'manager' || user.role === 'superuser')) {
    return <TimeclockAdmin />;
  }

  const activeEntry = activeData?.entry;
  const isClockedIn = !!activeEntry;

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <ClockIcon sx={{ fontSize: 40, mr: 2, color: '#2196F3' }} />
        <Box>
          <Typography variant="h4" fontWeight="bold">
            Timeclock
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Track your work hours
          </Typography>
        </Box>
      </Box>

      {/* Current Time Display */}
      <Card sx={{ mb: 3, background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
        <CardContent>
          <Typography variant="h3" align="center" sx={{ color: 'white', fontWeight: 600, fontFamily: 'monospace' }}>
            {format(currentTime, 'h:mm:ss a')}
          </Typography>
          <Typography variant="body2" align="center" sx={{ color: 'rgba(255,255,255,0.9)' }}>
            {format(currentTime, 'EEEE, MMMM d, yyyy')}
          </Typography>
        </CardContent>
      </Card>

      {/* Status Card */}
      {activeLoading ? (
        <Box display="flex" justifyContent="center" py={4}>
          <CircularProgress />
        </Box>
      ) : (
        <Card sx={{ mb: 3, border: isClockedIn ? '2px solid #4caf50' : '2px solid #9e9e9e' }}>
          <CardContent>
            <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
              <Box>
                <Typography variant="h6" gutterBottom>
                  Current Status
                </Typography>
                {isClockedIn ? (
                  <>
                    <Chip
                      icon={<ClockInIcon />}
                      label="CLOCKED IN"
                      color="success"
                      sx={{ fontWeight: 600, mb: 1 }}
                    />
                    <Typography variant="body2" color="text.secondary">
                      Clocked in at: {formatTimeInZone(activeEntry.clock_in, activeEntry.clock_in_timezone)}
                    </Typography>
                    <Typography variant="h5" sx={{ mt: 1, fontFamily: 'monospace', color: '#4caf50' }}>
                      {calculateElapsedTime(activeEntry.clock_in)}
                    </Typography>
                  </>
                ) : (
                  <>
                    <Chip
                      icon={<ClockOutIcon />}
                      label="CLOCKED OUT"
                      color="default"
                      sx={{ fontWeight: 600 }}
                    />
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                      Not currently clocked in
                    </Typography>
                  </>
                )}
              </Box>

              <Box sx={{ minWidth: 300 }}>
                {!isClockedIn ? (
                  <>
                    <TextField
                      fullWidth
                      size="small"
                      label="Notes (optional)"
                      value={notes}
                      onChange={(e) => setNotes(e.target.value)}
                      sx={{ mb: 2 }}
                      placeholder="Enter notes for this shift..."
                    />
                    <Button
                      fullWidth
                      variant="contained"
                      size="large"
                      startIcon={<ClockInIcon />}
                      onClick={handleClockIn}
                      disabled={clockInMutation.isPending}
                      sx={{
                        bgcolor: '#4caf50',
                        '&:hover': { bgcolor: '#45a049' },
                        fontWeight: 600,
                        py: 1.5,
                      }}
                    >
                      {clockInMutation.isPending ? 'Clocking In...' : 'CLOCK IN'}
                    </Button>
                  </>
                ) : (
                  <Button
                    fullWidth
                    variant="contained"
                    size="large"
                    startIcon={<ClockOutIcon />}
                    onClick={handleClockOut}
                    disabled={clockOutMutation.isPending}
                    sx={{
                      bgcolor: '#f44336',
                      '&:hover': { bgcolor: '#d32f2f' },
                      fontWeight: 600,
                      py: 1.5,
                    }}
                  >
                    {clockOutMutation.isPending ? 'Clocking Out...' : 'CLOCK OUT'}
                  </Button>
                )}
              </Box>
            </Box>

            {(clockInMutation.isError || clockOutMutation.isError) && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {clockInMutation.error?.message || clockOutMutation.error?.message || 'An error occurred'}
              </Alert>
            )}
          </CardContent>
        </Card>
      )}

      {/* Current Week Entries */}
      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom fontWeight="600">
          This Week's Time Entries
        </Typography>

        {weekLoading ? (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress />
          </Box>
        ) : weekData?.entries && weekData.entries.length > 0 ? (
          <>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell><strong>Date</strong></TableCell>
                    <TableCell><strong>Clock In</strong></TableCell>
                    <TableCell><strong>Clock Out</strong></TableCell>
                    <TableCell><strong>Hours</strong></TableCell>
                    <TableCell><strong>Notes</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {weekData.entries.map((entry: TimeEntry) => (
                    <TableRow key={entry.id}>
                      <TableCell>{formatDateInZone(entry.clock_in, entry.clock_in_timezone)}</TableCell>
                      <TableCell>{formatTimeInZone(entry.clock_in, entry.clock_in_timezone)}</TableCell>
                      <TableCell>
                        {entry.clock_out ? (
                          formatTimeInZone(entry.clock_out, entry.clock_out_timezone || entry.clock_in_timezone)
                        ) : (
                          <Chip label="Active" color="success" size="small" />
                        )}
                      </TableCell>
                      <TableCell>
                        {entry.hours_worked !== null ? (
                          <strong>{parseFloat(String(entry.hours_worked)).toFixed(2)} hrs</strong>
                        ) : (
                          <em>In progress</em>
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption" color="text.secondary">
                          {entry.notes || '-'}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            <Box sx={{ mt: 2, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
              <Typography variant="subtitle2" color="text.secondary">
                <strong>Total Hours This Week:</strong> {weekData.totalHours?.toFixed(2) || '0.00'} hours
              </Typography>
            </Box>
          </>
        ) : (
          <Alert severity="info">No time entries for this week yet.</Alert>
        )}
      </Paper>
    </Container>
  );
};

export default Timeclock;
